import dayjs, { Dayjs, isDayjs, ManipulateType, OpUnitType } from "dayjs";
import { isNil } from "lodash";
import { Geo } from "types";

type DateTypes = string | number | Date | Dayjs;

export const DayTimeStart = "T00:00:00";
export const DayTimeEnd = "T23:59:59";

export enum DateFormat {
    DAY = "ddd", // Wed, Thu etc.
    DAY_OF_MONTH = "DD", // 24
    MONTH = "MMM", // Dec
    YEAR = "YYYY", // 2021
    DATE = "ll", // Dec 29, 2021
    DATE_SHORT = "L", // 12/29/2021 (for American locale)
    DATE_LONG = "LL", // December 29, 2021
    DAY_TIME = "ddd LT", // Mon 6:00 PM
    TIME = "LT", // 8:49 AM
    TIME_TZ = "LT z (Z)", // 8:49 AM EDT (GMT+3), used for Feedback Widget
    TIME_24H = "HH:mm", // 20:49
    DATE_TIME = "LLL", // December 29, 2021 8:48 AM
    DATE_TIME_SHORT = "lll", // Dec 29, 2021 8:48 AM
    TIME_SHORT = "Hmm", // 700 for 7AM
    DATE_ISO = "YYYY-MM-DD",

    DATE_TIME_ISO = "YYYY-MM-DDTHH:mm:ssZ", // This is ISO8601 standard
    DATE_TIME_UTC_ISO = "YYYY-MM-DD[T]HH:mm:ss.SSS[Z]", // Used to for the Events API to create a timeslot

    DATE_TIME_ISO_END_DATE = "YYYY-MM-DD[T]23:59:59.000[Z]", // Used to for redemption api server, as it require end date in this format
    DATE_TIME_ISO_START_DATE_SELLER = "YYYY-MM-DD[T]00:00:00.000Z", // Used to packagers, fixed start date with seller tz
    DATE_TIME_ISO_END_DATE_SELLER = "YYYY-MM-DD[T]23:59:59.000Z", // Used to packagers, fixed end date with seller tz
    DATE_TIME_ISO_WITHOUT_TIMEZONE = "YYYY-MM-DD[T]HH:mm:ss", // used for custom report to match v1

    DEBUG = "YYYY-MM-DD HH:mm Z", // 2021-12-29 08:49 +05:30
    DAY_DATE = "ddd MMM D, YYYY", // Fri Sep 8, 2023
    DAY_WITH_FULL_MONTH_NAME_DATE = "ddd, MMMM D, YYYY", // Fri, September 8, 2023
    DATE_MONTH = "DD MMM", // 09 Apr
    MONTH_DATE = "MMM DD", // Apr 09
    MONTH_DATE_SHORT = "MMM D", // Apr 9
}

const isApprovedDateFormat = (format: DateFormat) => Object.values(DateFormat).includes(format);

const verifyDate = (date: DateTypes, format: DateFormat) => {
    if ((isNil(date) || date === "") && !import.meta.env.PROD) {
        console.groupCollapsed("formatDate - BUG: The provided date is null, empty or undefined", `Format: ${format}`);
        console.log("formatDate: Given date is", typeof date, date);
        console.error("formatDate: This function will return today's date which may not be what you want");
        console.groupEnd();

        return false;
    }

    return true;
};

const verifyDateFormat = (date: DateTypes, format: DateFormat) => {
    if (!isApprovedDateFormat(format)) {
        let message = `WARNING: The format "${format}" is not amongst Xola design approved formats\n  The approved formats are:\n`;
        for (const formatName of Object.keys(DateFormat)) {
            const formatString = DateFormat[formatName as keyof typeof DateFormat];
            message += `\tDateFormat.${formatName} = ${dayjs(date).format(formatString)}\n`;
        }

        console.error(message);
        return false;
    }

    return true;
};

/**
 * Format date according to the given format. This will also validate the given date, and format
 * Additionally: useTz flag if true will use dayjs.tz - Usually this should always be true
 *               If you are confused if this should be true/false - Ask Rushi
 */
export const formatDate = (date: DateTypes, format: DateFormat = DateFormat.DATE_ISO, useTZ?: boolean) => {
    if (verifyDateFormat(date, format) && verifyDate(date, format)) {
        try {
            const dateObj = dayjs.isDayjs(date) ? date : dayjs(date);
            const normalizedDate = useTZ ? dateObj.tz() : dateObj;

            return normalizedDate.format(format);
        } catch (error) {
            console.log(`Error formatting date ${date}`, error);
        }
    }

    return "_ERROR_FIX_DATE_";
};

/**
 * Take a Xola time format like 715 or 1534 and extract the hour and minute from it
 */
export const parseTime = (time: number) => {
    const hours = Math.floor(time / 100);
    const minutes = time % 100;
    return { hours, minutes };
};

const padNumber = (value: number) => value.toString().padStart(2, "0");

/**
 * This uses the date & time component and returns a string version of it without doing any timezone conversion
 * It's helpful when you convert the date in the JS date object to another value without using user's TZ
 */
export const dateToStr = (date: Date) => {
    const dateStr = `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())}`;
    const timeStr = `${padNumber(date.getHours())}:${padNumber(date.getMinutes())}:${padNumber(date.getSeconds())}`;
    return `${dateStr} ${timeStr}`;
};

/**
 * Convert the date in the Date object to the seller's timezone. If date is not provided, it returns the current time
 */
export const now = (date?: DateTypes | null, unit?: OpUnitType) => {
    if (!date) {
        if (unit) {
            return dayjs.tz().startOf(unit);
        }

        return dayjs.tz();
    }

    if (typeof date === "number") {
        const timestamp = date <= 2147483647 ? date * 1000 : date;
        return dayjs.tz(timestamp);
    }

    if (typeof date === "string" && date !== "_ERROR_FIX_DATE_") {
        return dayjs.tz(date);
    }

    if (isDayjs(date)) {
        // We do this late because under some conditions this is expensive (see: X2-9122)
        return date;
    }

    if (date instanceof Date && !isNaN(date.getTime())) {
        return dayjs.tz(dateToStr(date));
    }

    return dayjs.tz();
};

/**
 * Convert the date to local date and time without taking timezone offset into account.
 *
 * IMPORTANT: This is used for sending dates to FullCalendar and Xola's date picker which expects the date to be as a
 * JS date in the local timezone. So if date in seller timezone is 31 August 9PM (-04:00) and the user is in India
 * then we should send a JS Date of 31 August 9PM (+05:30) India TZ. This is because the date picker will show
 * 31 August to the _user_ who is in India. If it is not adjusted then the date picker or fullcalendar will
 * show a TZ adjusted value of 1st September
 *
 * The Date is built from the wall-clock parts rather than by converting the instant, because a JS Date reads its
 * local time from the browser's own tzdata, which dayjs plugins can't correct.
 */
export const toLocalDate = (date: Dayjs) => {
    return dayjs(
        new Date(date.year(), date.month(), date.date(), date.hour(), date.minute(), date.second(), date.millisecond()),
    );
};

const dateStringFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

export const isDateString = (value: string) => {
    return dateStringFormat.test(value) && dayjs(value).isValid();
};

export enum DurationUnit {
    DAY = "day",
    HOUR = "hour",
    MINUTE = "minute",
    SECOND = "second",
}

export enum NegativeOffset {
    BEFORE = "before",
    AFTER = "after",
}

export const getDuration = (minutes = 0, preferredUnit?: DurationUnit) => {
    if (minutes === 0 && preferredUnit) {
        return { value: minutes, unit: preferredUnit };
    }

    if (minutes % 1440 === 0) {
        return { value: minutes / 1440, unit: DurationUnit.DAY };
    }

    if (minutes % 60 === 0) {
        return { value: minutes / 60, unit: DurationUnit.HOUR };
    }

    return { value: minutes, unit: DurationUnit.MINUTE };
};

export const isBefore = (source: DateTypes, destination: DateTypes, unit: OpUnitType = "day") => {
    return now(source).isBefore(destination, unit);
};

export const isAfter = (source: DateTypes, destination: DateTypes, unit: OpUnitType = "day") => {
    return now(source).isAfter(destination, unit);
};

export const isSame = (source: DateTypes, destination: DateTypes, unit: OpUnitType = "day") => {
    return now(source).isSame(destination, unit);
};

/**
 * Check if the given date is in the past. By default, it will just check if the date is before this DAY
 */
export const isPast = (date: DateTypes, unit: OpUnitType = "day") => {
    return now(date).isBefore(now(), unit);
};

export const isFuture = (date: DateTypes, unit?: OpUnitType) => {
    const future = unit ? now().endOf(unit) : now();
    return now(date).isAfter(future);
};

export const isToday = (date: DateTypes) => {
    return now().isSame(date, "day");
};

export const getCurrentMonthInNumericFormat = (timezoneName: string) => {
    return dayjs().tz(timezoneName).month() + 1;
};

/**
 * Added instead of using .format("z") dayjs method since it returns GMT+-N instead of abbreviations for most timezones
 *
 * @param timezone Format "Eastern Standard Time", full timezone name, e.g. - dayjs().format("zzz")
 */
export const getTimezoneAbbreviation = (timezone: string) => {
    return timezone.replace(/(\w)\w*\W*/g, (_, index) => index.toUpperCase());
};

// The "timezoneId is string" is a type predicate. It tells TS that if this function returns true, then timezoneId must be a string.
// This is useful for type narrowing within conditional blocks where this function is used.
export const isValidTimeZoneId = (timezoneId?: string): timezoneId is string => {
    try {
        return timezoneId ? !!dayjs().tz(timezoneId) : false;
    } catch {
        return false;
    }
};

export const getTimezoneName = (date: Dayjs) => date.format("zzz");

/**
 * Formats response from api.geonames.org
 * @param timezones Format geonames array
 */
export const formatTimezoneList = (timezones: Array<{ geo: Geo }>) =>
    timezones?.map((timezone) => {
        const { geo } = timezone;
        const date = dayjs().tz(geo.timezone?.timeZoneId);
        const countryName = geo.countryName.replace("United States", "USA");

        return {
            value: geo.geonameId,
            label: `${geo.name}, ${countryName} (${getTimezoneAbbreviation(getTimezoneName(date))})`,
            geo,
        };
    });

/**
 * Get start & end dates when you need a range
 *
 * @param date
 * @param extendEnd
 */
export const getCalendarMonthRange = (date: Dayjs, extendEnd?: { count: number; unit: dayjs.ManipulateType }) => {
    let d = dayjs(date);

    // First day of the month and first day of the week of the first day of the month
    let start = d.startOf("month");
    start = start.startOf("week");

    let end = d.endOf("month");
    end = extendEnd ? end.endOf("week").add(extendEnd.count, extendEnd.unit) : end.endOf("week");

    return { start, end };
};

// TODO: Needs explanation this confuses me -Rushi
export const isDateBeforeNow = (date: Dayjs, offset: number) => {
    return dayjs(date.subtract(offset, "day")).isBefore(dayjs(now()), "day");
};

// TODO: Isn't this the same as isFuture, but at a time level
//       Used in Schedules and Gifts, Fix when refactoring that
export const isActiveDate = (date: string | Dayjs) => {
    return dayjs(date).diff(dayjs(), "day") >= 0;
};

// TODO: Use new now(). Fix when refactoring notifications drawer, there are a few automation tests
export const toDayJs = (value: DateTypes) => {
    return isDayjs(value) ? value : dayjs(value);
};

// TODO: Is used for arrivalDateTime.toString() and then getting time
//          This should be removed
export const convertAnyStringToFormattedTime = (timeString: string) => {
    if (/^\d{1}$/.test(timeString)) {
        return "000" + timeString;
    }

    if (/^\d{2}$/.test(timeString)) {
        return "00" + timeString;
    }

    if (/^\d{3}$/.test(timeString)) {
        return "0" + timeString;
    }

    if (/^\d{4}$/.test(timeString)) {
        return timeString;
    }

    const match = /^(\d{1,2})(?::(\d{2}))?\s*([APMapm]{2})?$/.exec(timeString);

    if (!match) {
        return "";
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let [_, hours, minutes = "00", period] = match;
    hours = parseInt(hours, 10).toString();

    if (period && period.toLowerCase() === "pm" && hours !== "12") {
        hours = (parseInt(hours, 10) + 12).toString();
    } else if (period && period.toLowerCase() === "am" && hours === "12") {
        hours = "0";
    }

    return `${hours.padStart(2, "0")}${minutes.padStart(2, "0")}`;
};

export const toDate = (date: Dayjs, isStartDate = true, useTz = true) => {
    const suffix = isStartDate ? DayTimeStart : DayTimeEnd;

    return new Date(formatDate(date, DateFormat.DATE_ISO, useTz) + suffix);
};

interface DateRange {
    start: Dayjs;
    end: Dayjs;
}

/**
 * Splits a date range into chunks of weeks based on the specified chunk size.
 *
 * @param startDate The start date of the range.
 * @param endDate The end date of the range.
 * @param chunkSize The desired size of each chunk in weeks. Defaults to 2 weeks if not provided.
 * @returns An array of DateRange objects representing the chunks of weeks within the date range.
 */
export const splitIntoWeeks = (startDate: Dayjs, endDate: Dayjs, chunkSize: number = 2): DateRange[] => {
    const weekRanges = [];
    let currentDay = startDate.startOf("week");

    while (currentDay <= endDate) {
        let endOfChunk = currentDay;

        // Iterate to find the end of the chunk based on the chunk size
        for (let i = 0; i < chunkSize; i++) {
            if (endOfChunk.add(1, "week").endOf("week") <= endDate) {
                endOfChunk = endOfChunk.add(1, "week").endOf("week");
            } else {
                break;
            }
        }

        const weekRange = { start: currentDay, end: endOfChunk };
        weekRanges.push(weekRange);

        currentDay = endOfChunk.add(1, "day");
    }

    return weekRanges;
};

export interface JsDateRange {
    from: Date;
    to?: Date | null;
}

export const clampFutureDateRange = (value: JsDateRange, max: Dayjs) => {
    const newValue = { ...value };

    const currentFrom = now(value.from);
    if (newValue.from && currentFrom.isAfter(max)) {
        // We don't bother with setting the hour, month etc. because we expect that to be
        // already properly set in the date.
        newValue.from.setMonth(max.month(), max.date());
        newValue.from.setFullYear(max.year());
    }

    const currentTo = now(value.to);
    if (newValue.to && currentTo.isAfter(max)) {
        // Same as about but for 'to'.

        // Set max month along with the date (same for 'from'),
        // if you set max date on 31th it will shift to the next month,
        // or if you set month to the one with 30 days while the date is 31,
        // e.g. today May 31, max is May 31, value is Jun 30, it will set Jun 30 to 31 day,
        // resulting in Jul 01, then month to max, returning May 1 instead of May 31.
        newValue.to.setMonth(max.month(), max.date());
        newValue.to.setFullYear(max.year());
    }

    return newValue;
};

/**
 * Allows you to specify time in a human manner and return the time in milliseconds
 * This is so there are no typos. `duration(5, "minutes")` is easier than writing 30000 ms
 */
export const duration = (value: number, unit: ManipulateType): number => {
    return dayjs.duration(value, unit).asMilliseconds();
};

// dayjs parses the date but returns the same time, so need to convert the time to seller's timezone
export const utcToDate = (value: string, timezoneName: string) => {
    return dayjs(value).tz(timezoneName);
};

export const getMonthStartEndRange = (date: Dayjs) => {
    const start = date.startOf("month");
    const end = date.endOf("month");
    return { start, end };
};

export const getWeekStartEndRange = (date: Dayjs) => {
    const start = date.startOf("week");
    const end = date.endOf("week");
    return { start, end };
};

export const formatAMPM = (time: { hours: number; minutes: number }) => {
    let { hours, minutes } = time;
    const ampm = hours >= 12 ? "PM" : "AM";

    hours %= 12;
    const formattedHours = hours ? hours : 12; // the hour '0' should be '12'
    const formattedMinutes = minutes < 10 ? "0" + minutes.toString() : minutes.toString();
    return formattedHours.toString() + ":" + formattedMinutes.toString() + " " + ampm.toString();
};
